import { useState } from "react";
import { Link } from "react-router-dom";
import { LogoCompact } from "../Logo/Logo";
import { useLayoutConfig } from "./LayoutContext";
import { BLOG_ENABLED } from "../../content/blogPosts/enabled";

const FOOTER_SECTIONS = [
  {
    title: "Products",
    links: [
      {
        label: "WritersProof",
        href: "https://writersproof.com",
        external: true,
      },
      { label: "Scrivener MCP", href: "/scrivener-mcp", external: false },
      {
        label: "WARM",
        href: "/warm",
        external: false,
      },
      { label: "AI Agents", href: "/agents", external: false },
      { label: "Compare Approaches", href: "/compare", external: false },
      { label: "Pricing", href: "/pricing", external: false },
      { label: "OnRecord Studio", href: "/onrecord", external: false },
    ],
  },
  {
    title: "For Writers",
    links: [
      { label: "For Authors", href: "/for-authors", external: false },
      { label: "For Students", href: "/for-students", external: false },
      { label: "For Publishers", href: "/for-publishers", external: false },
      {
        label: "Download WritersProof",
        href: "https://writersproof.com",
        external: true,
      },
      {
        label: "Verify Evidence",
        href: "https://writersproof.com/verify",
        external: true,
      },
    ],
  },
  {
    title: "Developers",
    links: [
      { label: "Protocol Specification", href: "/protocol", external: false },
      { label: "Security Model", href: "/security-model", external: false },
      { label: "Identity & Discovery Files", href: "/.well-known/", external: false },
      {
        label: "GitHub",
        href: "https://github.com/writerslogic",
        external: true,
      },
    ],
  },
  {
    title: "Resources",
    links: [
      ...(BLOG_ENABLED
        ? [{ label: "Blog", href: "/blog", external: false }]
        : []),
      {
        label: "Longhand",
        href: "https://writersproof.com/story",
        external: true,
      },
      {
        label: "Executive Summary",
        href: "/executive-summary",
        external: false,
      },
      { label: "Case Studies", href: "/case-studies", external: false },
      {
        label: "IETF Drafts",
        href: "https://datatracker.ietf.org/doc/search?name=condrey",
        external: true,
      },
      { label: "Compliance", href: "/compliance", external: false },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "/about", external: false },
      { label: "Press", href: "/press", external: false },
      { label: "Partnerships", href: "/partnerships", external: false },
      { label: "Careers", href: "/careers", external: false },
      { label: "Privacy Policy", href: "/privacy", external: false },
      { label: "Terms of Service", href: "/terms", external: false },
      {
        label: "Contact Us",
        href: "mailto:david@writerslogic.com",
        external: true,
      },
      { label: "(816) WRITERS", href: "tel:+18169748377", external: true },
    ],
  },
];

function FooterLink({
  link,
}: {
  link: (typeof FOOTER_SECTIONS)[0]["links"][0];
}) {
  const cls =
    "inline-flex items-center min-h-[44px] text-sm text-[var(--text-muted)] hover:text-[var(--accent-primary)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]/40 rounded";

  if (link.external) {
    return (
      <a
        href={link.href}
        className={cls}
        {...(!/^(mailto:|tel:)/.test(link.href)
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {link.label}
        {!/^(mailto:|tel:)/.test(link.href) && (
          <span className="sr-only"> (opens in new tab)</span>
        )}
      </a>
    );
  }
  return (
    <Link
      to={link.href}
      reloadDocument={link.href.startsWith("/.well-known/")}
      className={cls}
    >
      {link.label}
    </Link>
  );
}

function FooterSection({ section }: { section: (typeof FOOTER_SECTIONS)[0] }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="footer-section border-b border-[var(--border-color)] sm:border-0">
      {/* On mobile: accordion trigger. On sm+: plain heading. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="footer-section__trigger w-full flex items-center justify-between py-4 sm:py-0 sm:mb-4 text-left sm:cursor-default select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]/40 rounded"
        aria-expanded={open}
        aria-controls={`footer-links-${section.title}`}
      >
        <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--editor-text)]">
          {section.title}
        </h3>
        {/* Chevron: visible only on mobile */}
        <svg
          className={`footer-section__chevron w-4 h-4 text-[var(--text-muted)] transition-transform duration-200 sm:hidden ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Link list: collapsible on mobile, always visible on sm+ */}
      <div
        id={`footer-links-${section.title}`}
        className={`grid transition-[grid-template-rows] duration-200 ease-out sm:grid-rows-[1fr] ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <ul className="overflow-hidden space-y-1 sm:pb-0 pb-4">
          {section.links.map((link) => (
            <li key={link.label}>
              <FooterLink link={link} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function WritersLogicFooter() {
  const { config } = useLayoutConfig();
  const showReassurance = config.showPrivacyReassurance !== false;

  return (
    <footer
      className="pt-12 pb-8 section-x"
      style={{
        borderTop: "1px solid var(--border-color)",
        background:
          "color-mix(in srgb, var(--panel-bg) 55%, var(--surface-dark))",
      }}
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="max-w-7xl mx-auto">
        {/* Multi-column navigation — accordion on mobile, grid on sm+ */}
        <nav
          className="mb-12 sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 sm:gap-10 lg:gap-12"
          aria-label="Footer navigation"
        >
          {FOOTER_SECTIONS.map((section) => (
            <FooterSection key={section.title} section={section} />
          ))}
        </nav>

        {/* Privacy reassurance */}
        {showReassurance && (
          <div className="mb-8 py-6 border-t border-b border-[var(--border-color)] text-center">
            <p className="text-[var(--text-muted)]">
              We never store, share, or use your content for AI training. Your
              writing remains yours.
            </p>
          </div>
        )}

        {/* Social links */}
        <div className="flex justify-center gap-5 mb-8">
          <a
            href="https://github.com/writerslogic"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--text-muted)] hover:text-[var(--accent-primary)] transition-colors"
            aria-label="GitHub"
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                clipRule="evenodd"
              />
            </svg>
          </a>
          <a
            href="https://x.com/davidcondrey"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--text-muted)] hover:text-[var(--accent-primary)] transition-colors"
            aria-label="X (Twitter)"
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <a
            href="https://linkedin.com/company/writerslogic"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--text-muted)] hover:text-[var(--accent-primary)] transition-colors"
            aria-label="LinkedIn"
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
        </div>

        {/* Provenance disclosure — we practice the voluntary provenance we sell:
            the site copy was AI-assisted, and we say so. */}
        <div className="mb-8 text-center">
          <p className="text-sm text-[var(--text-muted)]">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full align-middle mr-2"
              style={{ background: "var(--color-teal-400, #3A9E8E)" }}
              aria-hidden="true"
            />
            This site&rsquo;s copy was written with AI assistance and edited by
            a human.{" "}
            <Link
              to="/colophon"
              className="underline underline-offset-2 hover:text-[var(--accent-primary)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]/40 rounded"
            >
              Read the colophon
            </Link>
            .
          </p>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-[var(--border-color)] text-[var(--text-muted)] text-center md:text-left">
          <div className="flex items-center gap-3">
            <LogoCompact />
            <p>
              &copy; {new Date().getFullYear()} WritersLogic, Inc. All rights
              reserved.
            </p>
          </div>

          {!showReassurance && (
            <p className="text-center max-w-lg">
              We never store, share, or use your content for AI training. Your
              writing remains yours.
            </p>
          )}
        </div>
      </div>
    </footer>
  );
}
